import type { ParallelPerfectPair, VoiceLeadingIdiom, VoicedChord } from '@tunasong/music-lib'
import { classifyVoiceMotion, detectParallelPerfectPairs, semitoneDistance } from '@tunasong/music-lib'
import type { NoteName } from '@tunasong/schemas'
import type { FC } from 'react'
import styles from './voicing-piano-roll.module.css'

export interface VoicingPianoRollProps {
  /** Exactly 2 voiced chords (low → high pitches each). */
  voicing: readonly VoicedChord[]
  /** Optional chord labels rendered above each column (e.g. ['I', 'V']). */
  columnLabels?: readonly [string, string]
  /**
   * Active idiom. Parallel perfect 5ths/8vas are highlighted ONLY when 'classical',
   * matching the scorer (jazz/pop do not penalise parallels). Omit to suppress the
   * highlight entirely (presentational / Storybook default).
   */
  idiom?: VoiceLeadingIdiom
  className?: string
}

// y-axis covers the Bowie-locked range [C2, B4] across all 12 keys × 4 pairs.
// Low bound is C2 (not G2): the IV→V awkward bass reaches F2 in C major and
// C2 transposed to G major — both below G2 (scope memo §224). A G2 floor would
// clip those rects below the plot, breaking the parallel-fifths trap pair.
const Y_AXIS_LOW: NoteName = 'C2' as NoteName
const Y_AXIS_HIGH: NoteName = 'B4' as NoteName
const AXIS_SPAN = semitoneDistance(Y_AXIS_LOW, Y_AXIS_HIGH) // 35 semitones

const VOICE_LANE_CLASS = ['voiceBass', 'voiceMid', 'voiceTop'] as const

/** 0 % = top of the plot (B4); 100 % = bottom (G2). */
const yPercent = (pitch: NoteName): number =>
  ((AXIS_SPAN - semitoneDistance(Y_AXIS_LOW, pitch)) / AXIS_SPAN) * 100

const motionClass = (from: NoteName, to: NoteName): string => {
  const motion = classifyVoiceMotion(from, to)
  if (motion === 'common-tone') {
    return styles.commonToneHeld
  }
  if (motion === 'leap') {
    return styles.leap
  }
  return styles.step
}

const VOICE_NAME = ['Bass', 'Middle', 'Top'] as const

const voiceName = (voiceIndex: number): string => VOICE_NAME[voiceIndex] ?? `Voice ${voiceIndex + 1}`

/** Screen-reader phrase naming the concept (not pixels) for one voice's motion. */
const motionPhrase = (voiceIndex: number, from: NoteName, to: NoteName): string => {
  const name = voiceName(voiceIndex)
  const motion = classifyVoiceMotion(from, to)
  if (motion === 'common-tone') {
    return `${name}: ${from} held (common tone).`
  }
  const direction = semitoneDistance(from, to) > 0 ? 'up' : 'down'
  return `${name}: ${motion} ${direction}, ${from} to ${to}.`
}

/** Spoken phrase for a flagged parallel pair, e.g. "Parallel fifths between Bass and Top." */
const parallelPhrase = (pair: ParallelPerfectPair): string => {
  const interval = pair.kind === 'fifth' ? 'fifths' : 'octaves'
  return `Parallel ${interval} between ${voiceName(pair.lower)} and ${voiceName(pair.upper)}.`
}

const parallelGlyph = (kind: ParallelPerfectPair['kind']): string => (kind === 'fifth' ? '∥5' : '∥8')

// Plot x-anchors (viewBox 0 0 100 100): column A rects span 8–36, column B 64–92.
const COL_A_X = 8
const COL_B_X = 92

// Shared pitch gridlines: one per chromatic step would be noisy at 35 semitones,
// so draw a calmer grid every 6 semitones (tritone) across the [C2, B4] span.
const GRIDLINE_STEP = 6
const GRIDLINE_COUNT = Math.floor(AXIS_SPAN / GRIDLINE_STEP)

const MOTION_LEGEND = [
  { className: 'commonToneHeld', label: 'Held (common tone)' },
  { className: 'step', label: 'Steps (by a 2nd)' },
  { className: 'leap', label: 'Leaps (3rd or more)' },
] as const

/**
 * The first Tunasong pitch-visualization component. Renders a 2-column piano
 * roll for a voice-leading pair: each voice gets a note rect per chord plus a
 * connecting motion line coloured by motion type (common-tone / step / leap).
 *
 * v1 ships a functional default using design tokens; Joni iterates on the
 * Storybook entries post-v1 (M3.2).
 *
 * Spec: docs/superpowers/specs/2026-05-27-voice-leading-lab-v1-design.md §8.5
 */
export const VoicingPianoRoll: FC<VoicingPianoRollProps> = props => {
  const { voicing, columnLabels, idiom, className } = props
  if (voicing.length !== 2) {
    return <div className={styles.empty}>Voicing must have exactly 2 chords.</div>
  }
  const [a, b] = voicing
  const voiceCount = Math.min(a.pitches.length, b.pitches.length)

  // Idiom gate: only classical penalises parallels, so only classical flags them.
  const parallelPairs = idiom === 'classical' ? detectParallelPerfectPairs(a, b) : []

  const transition = columnLabels ? `${columnLabels[0]} to ${columnLabels[1]}` : 'chord pair'
  const motionSummary = Array.from({ length: voiceCount }, (_, v) =>
    motionPhrase(v, a.pitches[v], b.pitches[v])
  ).join(' ')
  const parallelSummary = parallelPairs.map(parallelPhrase).join(' ')
  const ariaLabel = `Voice motion, ${transition}. ${motionSummary}${parallelSummary ? ` ${parallelSummary}` : ''}`

  return (
    <div className={className ? `${styles.root} ${className}` : styles.root}>
      {columnLabels ? (
        <div className={styles.columnLabels} aria-hidden="true">
          <span>{columnLabels[0]}</span>
          <span>{columnLabels[1]}</span>
        </div>
      ) : null}
      <svg
        className={styles.svg}
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        role="img"
        aria-label={ariaLabel}
      >
        {Array.from({ length: GRIDLINE_COUNT + 1 }, (_, g) => {
          const y = (g / GRIDLINE_COUNT) * 100
          return (
            <line
              key={`grid-${g}`}
              data-testid="vl-pitch-gridline"
              className={styles.gridline}
              x1={0}
              y1={y}
              x2={100}
              y2={y}
            />
          )
        })}
        {Array.from({ length: voiceCount }, (_, v) => (
          <g key={v} data-testid="vl-voice-lane" className={styles[VOICE_LANE_CLASS[v] ?? 'voiceMid']}>
            <rect data-testid="vl-note-rect" x={8} y={yPercent(a.pitches[v]) - 2} width={28} height={4} />
            <rect data-testid="vl-note-rect" x={64} y={yPercent(b.pitches[v]) - 2} width={28} height={4} />
            <line
              data-testid="vl-voice-motion-line"
              x1={36}
              y1={yPercent(a.pitches[v])}
              x2={64}
              y2={yPercent(b.pitches[v])}
              className={motionClass(a.pitches[v], b.pitches[v])}
            />
          </g>
        ))}
        {parallelPairs.map(pair => {
          const aLow = yPercent(a.pitches[pair.lower])
          const aHigh = yPercent(a.pitches[pair.upper])
          const bLow = yPercent(b.pitches[pair.lower])
          const bHigh = yPercent(b.pitches[pair.upper])
          const glyphY = (Math.min(aLow, aHigh, bLow, bHigh) + Math.max(aLow, aHigh, bLow, bHigh)) / 2
          return (
            <g
              key={`par-${pair.lower}-${pair.upper}`}
              data-testid="vl-parallel-span"
              className={styles.parallelSpan}
              role="img"
              aria-label={parallelPhrase(pair)}
            >
              {/* bracket on each column connecting the two offending voices */}
              <line x1={COL_A_X} y1={aLow} x2={COL_A_X} y2={aHigh} className={styles.parallelBracket} />
              <line x1={COL_B_X} y1={bLow} x2={COL_B_X} y2={bHigh} className={styles.parallelBracket} />
              {/* the two held-in-parallel voice motions, spanning both columns */}
              <line x1={COL_A_X} y1={aLow} x2={COL_B_X} y2={bLow} className={styles.parallelTie} />
              <line x1={COL_A_X} y1={aHigh} x2={COL_B_X} y2={bHigh} className={styles.parallelTie} />
              <text x={50} y={glyphY} className={styles.parallelGlyph} textAnchor="middle" dominantBaseline="central">
                {parallelGlyph(pair.kind)}
              </text>
            </g>
          )
        })}
      </svg>
      <ul className={styles.legend} aria-label="Motion legend">
        {MOTION_LEGEND.map(item => (
          <li key={item.className} className={styles.legendItem}>
            <svg className={styles.legendSwatch} viewBox="0 0 24 8" aria-hidden="true">
              <line x1={0} y1={4} x2={24} y2={4} className={styles[item.className]} />
            </svg>
            <span>{item.label}</span>
          </li>
        ))}
      </ul>
      <div className={styles.columns}>
        {[a, b].map((chord, c) => (
          <div key={c} data-testid="vl-chord-column" className={styles.column}>
            {chord.pitches.map((p, v) => (
              <span key={v} className={styles[VOICE_LANE_CLASS[v] ?? 'voiceMid']}>
                {p}
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}
